import { useState } from "react";
import { GripHorizontal, KanbanSquare } from "lucide-react";
import { getTaskStateLabel, type Task, type TaskGroup } from "../types/tasks";
import { TaskCard } from "./TaskCard";

type Props = {
  groups: TaskGroup[];
  canReorder: boolean;
  onOpenTask: (task: Task) => void;
  onReorder: (ids: number[]) => Promise<void>;
};

export function TaskKanbanBoard({
  groups,
  canReorder,
  onOpenTask,
  onReorder,
}: Props) {
  const [dragState, setDragState] = useState<{
    taskId: number;
    groupKey: string;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    groupKey: string;
    taskId: number;
    position: "before" | "after";
  } | null>(null);

  const moveWithinGroup = async (
    group: TaskGroup,
    targetId: number | null,
    position: "before" | "after" | "end",
  ) => {
    if (!dragState || dragState.groupKey !== group.key) return;

    const ids = group.items.map((item) => item.id);
    const fromIndex = ids.indexOf(dragState.taskId);
    if (fromIndex === -1) return;

    const next = [...ids];
    next.splice(fromIndex, 1);

    if (position === "end" || targetId === null) {
      next.push(dragState.taskId);
    } else {
      const targetIndex = next.indexOf(targetId);
      if (targetIndex === -1) return;
      const insertAt = position === "before" ? targetIndex : targetIndex + 1;
      next.splice(insertAt, 0, dragState.taskId);
    }

    if (next.join(",") === ids.join(",")) return;

    setDropTarget(null);
    setDragState(null);
    await onReorder(next);
  };

  return (
    <div className="tasks-board-grid">
      {groups.map((group, index) => (
        <section
          key={group.key}
          className="overflow-hidden rounded-[34px] border border-[#dfe8e4] bg-white/94 shadow-[0_24px_70px_rgba(15,23,42,.09)] backdrop-blur-xl"
          style={{ animationDelay: `${index * 70}ms` }}
        >
          <header className="relative overflow-hidden border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)] px-5 py-4">
            <div className="pointer-events-none absolute -right-16 top-[-70px] h-40 w-40 rounded-full bg-[#00d09c]/10 blur-3xl" />
            <span className="absolute inset-x-5 top-0 h-1 rounded-b-full bg-gradient-to-r from-[#0b4a2f] to-[#00d09c]" />

            <div className="relative flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl border border-[#00d09c]/18 bg-white text-[#0b4a2f] shadow-[0_10px_24px_rgba(15,23,42,.06)]">
                  <KanbanSquare size={18} />
                </span>

                <div>
                  <div className="text-sm font-black text-[#0b4a2f]">
                    {getTaskStateLabel(group.key)}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-500">
                    {group.items.length} tarea(s)
                  </div>
                </div>
              </div>

              <span className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-3 py-1 text-xs font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.045)]">
                {canReorder && <GripHorizontal size={13} />}
                {group.items.length}
              </span>
            </div>
          </header>

          <div
            className="tasks-column-scroll flex min-h-[220px] flex-col gap-3 bg-[radial-gradient(circle_at_top_left,rgba(0,208,156,.035),transparent_28%),#f8fafc] p-4"
            onDragOver={(event) => {
              if (!canReorder || dragState?.groupKey !== group.key) return;
              event.preventDefault();
              setDropTarget(null);
            }}
            onDrop={(event) => {
              if (!canReorder || dragState?.groupKey !== group.key) return;
              event.preventDefault();
              void moveWithinGroup(group, null, "end");
            }}
          >
            {group.items.length === 0 ? (
              <div className="grid min-h-[180px] place-items-center rounded-[26px] border border-dashed border-[#dfe8e4] bg-white/70 p-6 text-center text-sm font-semibold text-slate-400">
                Sin tareas en este estado.
              </div>
            ) : (
              group.items.map((task) => (
                <div
                  key={task.id}
                  draggable={canReorder}
                  data-drop-position={
                    dropTarget?.groupKey === group.key &&
                    dropTarget.taskId === task.id
                      ? dropTarget.position
                      : undefined
                  }
                  onDragStart={() => {
                    if (!canReorder) return;
                    setDragState({ taskId: task.id, groupKey: group.key });
                  }}
                  onDragEnd={() => {
                    setDragState(null);
                    setDropTarget(null);
                  }}
                  onDragOver={(event) => {
                    if (!canReorder || dragState?.groupKey !== group.key) return;
                    event.preventDefault();
                    const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const position =
                      event.clientY < rect.top + rect.height / 2 ? "before" : "after";
                    setDropTarget({
                      groupKey: group.key,
                      taskId: task.id,
                      position,
                    });
                  }}
                  onDrop={(event) => {
                    if (!canReorder || dragState?.groupKey !== group.key) return;
                    event.preventDefault();
                    const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const position =
                      event.clientY < rect.top + rect.height / 2 ? "before" : "after";
                    void moveWithinGroup(group, task.id, position);
                  }}
                  className={[
                    dropTarget?.groupKey === group.key &&
                    dropTarget.taskId === task.id &&
                    dropTarget.position === "before"
                      ? "border-t-4 border-t-[#00d09c]"
                      : "",
                    dropTarget?.groupKey === group.key &&
                    dropTarget.taskId === task.id &&
                    dropTarget.position === "after"
                      ? "border-b-4 border-b-[#00d09c]"
                      : "",
                  ].join(" ")}
                >
                  <TaskCard task={task} onOpen={onOpenTask} />
                </div>
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
